import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Filter, Search, X } from 'lucide-react';
import type { JobStatus, VehicleRequired, Urgency } from '@godrop/types';
import { api, ApiError } from '../api';
import { Shell } from '../components/layout/Shell';
import { PageHeader } from '../components/layout/PageHeader';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { StatusChip } from '../components/ui/status-chip';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Skeleton } from '../components/ui/skeleton';
import { EmptyState } from '../components/ui/empty-state';
import { fmtMoney, fmtRelative } from '../lib/utils';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

interface AdminJobRow {
  id: string;
  code: string;
  title: string;
  status: JobStatus;
  vehicle_required: VehicleRequired;
  urgency: Urgency;
  payout: number;
  pickup_area: string;
  dropoff_area: string;
  poster_phone: string;
  poster_name: string | null;
  courier_phone: string | null;
  courier_name: string | null;
  pod_image_urls: string[];
  pod_uploaded_at: string | null;
  created_at: string;
  accepted_at: string | null;
}

const STATUSES: JobStatus[] = ['open', 'accepted', 'completed', 'cancelled', 'expired'];
const VEHICLES: VehicleRequired[] = ['walker', 'bicycle', 'motorcycle', 'car', 'van', 'lorry', 'any'];
const URGENCIES: Urgency[] = ['standard', 'urgent'];

export function JobsPage() {
  const [params, setParams] = useSearchParams();
  const status = (params.get('status') ?? '') as JobStatus | '';
  const vehicle = (params.get('vehicle') ?? '') as VehicleRequired | '';
  const urgency = (params.get('urgency') ?? '') as Urgency | '';
  const q = params.get('q') ?? '';

  const [rows, setRows] = useState<AdminJobRow[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [draftQ, setDraftQ] = useState(q);

  function patch(next: Partial<Record<'status' | 'vehicle' | 'urgency' | 'q', string>>) {
    const merged = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) {
      if (v == null || v === '') merged.delete(k);
      else merged.set(k, v);
    }
    setParams(merged);
  }

  function load() {
    setRows(null);
    setErr(null);
    const qs = new URLSearchParams();
    if (status) qs.set('status', status);
    if (vehicle) qs.set('vehicle', vehicle);
    if (urgency) qs.set('urgency', urgency);
    if (q) qs.set('q', q);
    api<{ jobs: AdminJobRow[] }>(`/api/admin/jobs${qs.toString() ? `?${qs}` : ''}`)
      .then((r) => setRows(r.jobs))
      .catch((e) => setErr(e instanceof ApiError ? `Error ${e.status}` : 'Could not load'));
  }

  useEffect(load, [status, vehicle, urgency, q]);

  const hasFilters = Boolean(status || vehicle || urgency || q);

  return (
    <Shell title="Drops">
      <PageHeader
        title="All drops"
        description="Browse, filter, and inspect every drop posted on GoDrop."
      />

      <Card className="mb-4">
        <CardContent className="flex flex-col gap-3 p-4 md:flex-row md:items-center md:gap-2">
          <form
            className="relative flex-1"
            onSubmit={(e) => {
              e.preventDefault();
              patch({ q: draftQ });
            }}
          >
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-muted" />
            <Input
              value={draftQ}
              onChange={(e) => setDraftQ(e.target.value)}
              placeholder="Search code, title, or area…"
              className="pl-9"
            />
          </form>
          <div className="grid grid-cols-2 gap-2 md:flex md:gap-2">
            <Select value={status || 'all'} onValueChange={(v) => patch({ status: v === 'all' ? '' : v })}>
              <SelectTrigger className="md:w-36"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={vehicle || 'all'} onValueChange={(v) => patch({ vehicle: v === 'all' ? '' : v })}>
              <SelectTrigger className="md:w-36"><SelectValue placeholder="Vehicle" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any vehicle</SelectItem>
                {VEHICLES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={urgency || 'all'} onValueChange={(v) => patch({ urgency: v === 'all' ? '' : v })}>
              <SelectTrigger className="md:w-36"><SelectValue placeholder="Urgency" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any urgency</SelectItem>
                {URGENCIES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
            {hasFilters && (
              <Button
                variant="ghost"
                size="sm"
                className="md:h-9"
                onClick={() => {
                  setDraftQ('');
                  setParams(new URLSearchParams());
                }}
              >
                <X className="h-3.5 w-3.5" /> Reset
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {err && (
        <Card className="mb-4 border-error/40 bg-error-tint">
          <CardContent className="py-3 text-sm text-error-on-tint">{err}</CardContent>
        </Card>
      )}

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Code</TableHead>
              <TableHead>Title</TableHead>
              <TableHead>Route</TableHead>
              <TableHead>Vehicle</TableHead>
              <TableHead className="text-right tnum">Payout</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Poster</TableHead>
              <TableHead>Courier</TableHead>
              <TableHead>POD</TableHead>
              <TableHead className="text-right">Created</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {!rows && Array.from({ length: 6 }).map((_, i) => (
              <TableRow key={i}>
                <TableCell colSpan={10}><Skeleton className="h-4 w-full" /></TableCell>
              </TableRow>
            ))}
            {rows && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={10}>
                  <EmptyState
                    icon={Filter}
                    title={hasFilters ? 'No drops match these filters.' : 'No drops yet.'}
                  />
                </TableCell>
              </TableRow>
            )}
            {rows?.map((j) => (
              <TableRow key={j.id} className="cursor-pointer">
                <TableCell className="tnum text-xs font-semibold text-text-subtle">
                  <Link to={`/jobs/${j.id}`}>{j.code}</Link>
                </TableCell>
                <TableCell className="font-medium">
                  <Link to={`/jobs/${j.id}`} className="hover:text-brand-700 dark:hover:text-brand-500">
                    {j.title}
                  </Link>
                </TableCell>
                <TableCell className="text-text-subtle">
                  <Link to={`/jobs/${j.id}`}>{j.pickup_area} → {j.dropoff_area}</Link>
                </TableCell>
                <TableCell>
                  <Link to={`/jobs/${j.id}`}><Badge>{j.vehicle_required}</Badge></Link>
                </TableCell>
                <TableCell className="text-right tnum font-semibold text-brand-700 dark:text-brand-500">
                  <Link to={`/jobs/${j.id}`}>{fmtMoney(j.payout)}</Link>
                </TableCell>
                <TableCell>
                  <Link to={`/jobs/${j.id}`} className="flex items-center gap-1">
                    <StatusChip status={j.status} />
                    {j.urgency === 'urgent' && <Badge variant="urgent">urgent</Badge>}
                  </Link>
                </TableCell>
                <TableCell className="text-xs text-text-subtle">
                  <Link to={`/users/${''}`} className="block min-w-0 truncate">
                    {j.poster_name ?? j.poster_phone}
                  </Link>
                </TableCell>
                <TableCell className="text-xs text-text-subtle">
                  {j.courier_phone ? (j.courier_name ?? j.courier_phone) : '—'}
                </TableCell>
                <TableCell>
                  {(() => {
                    const urls = j.pod_image_urls ?? [];
                    if (urls.length === 0) return <span className="text-xs text-text-muted">—</span>;
                    return (
                      <Badge variant="completed">
                        uploaded
                        {urls.length > 1 ? ` (${urls.length})` : ''}
                      </Badge>
                    );
                  })()}
                </TableCell>
                <TableCell className="text-right text-xs text-text-muted">
                  {fmtRelative(j.created_at)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </Shell>
  );
}
